/**
 * gRPC error-path -> form field-path aliases, shared by every validation flow.
 *
 * This table existed TWICE as an inline literal in DocumentProcessing.tsx: one
 * copy inside runAutoValidation (which populates the Errors tab) and one inside
 * handleSendData (submission). They drifted badly - 150 entries versus 304, with
 * 196 aliases present only on the submit path. The consequence was that an error
 * submission could attribute to a field was left unattributed in the UI: counted
 * in the badge, rendered nowhere, impossible to fix from the Errors tab. Package
 * Type (27 errors on the 228407 proforma) and document type reached the user that
 * way.
 *
 * One table, imported by both, so a name added for one flow serves the other.
 *
 * The single conflicting key was 'beforderungsroute': the Errors-tab copy mapped
 * it to kopf.beforderungsroute.ausgewahlteLander (a real field in
 * AtlasValidationPreview) and the submit copy to sendung.beforderungsroute (a
 * label-only entry, not a field). The real field wins.
 */
export const GRPC_FIELD_PATH_ALIASES: Record<string, string> = {
  // ── Errors that reached no field and so were never shown ────────────────
  // The Errors tab read "Errors (0)" while the validator reported 7 issues.
  // A required field the user cannot see is a required field they cannot fill,
  // so every name the validator emits needs a home.
  //
  // geschArt: the validator's short name for the nature of transaction.
  // MEASURED which field it actually reads, by setting each alone to "12":
  //   kopf.angabenGeschaftsvorgangArt -> 3 errors remain (ATLAS IGNORES it)
  //   kopf.artdesGeschafts            -> 0 errors
  //   position[i].artdesGeschafts     -> 0 errors (lifted to the header)
  // An earlier version of this alias pointed at angabenGeschaftsvorgangArt, so
  // the error landed on a field ATLAS does not read: the user selected a value,
  // the form showed it, and "Required data field" never cleared because the
  // real field was still empty.
  'kopf.geschArt': 'kopf.artdesGeschafts',
  'geschArt': 'kopf.artdesGeschafts',
  'kopf.angabenGeschaftsvorgangArt': 'kopf.artdesGeschafts',
  // transportausrustung_messages: raised when containerIndikator is "1" but no
  // container is declared. The nearest field the user can act on is the
  // container indicator itself — clearing it, or supplying the container,
  // resolves the requirement.
  'kopf.transportausrustung_messages': 'kopf.containerIndikator',
  'transportausrustung_messages': 'kopf.containerIndikator',
  'kopf.transportausrustung': 'kopf.containerIndikator',
  'MassgeblichesDateField': 'kopf.massgeblichesDatum',
  'angemeldetes': '__fanout_beantragtesVerfahren__',
  'angemeldetes verfahren': '__fanout_beantragtesVerfahren__',
  'anmeldeart': 'kopf.anmeldeart',
  'anmelder': 'kopf.anmelder',
  'anmelder.ansprechName': 'kopf.anmelder.ansprechName',
  'anmelder.ansprechpartnerName': 'kopf.anmelder.ansprechName',
  'anmelder.name': 'kopf.anmelder.beauftragterName',
  'anmelderAnsprechName': 'kopf.anmelder.ansprechName',
  'anmelderAnsprechpartnerName': 'kopf.anmelder.ansprechName',
  'anmelderName': 'kopf.anmelder.beauftragterName',
  'anmelderNiederlassungsnummer': 'kopf.anmelder.niederlassungsNummer',
  'anmelderSachbearbeiter': 'kopf.anmelder.ansprechName',
  'anmelderTelefon': 'kopf.anmelder.phone',
  'anmelderTelefonNummer': 'kopf.anmelder.phone',
  'anmelderTin': 'kopf.anmelder.tin',
  'anmeldername': 'kopf.anmelder.beauftragterName',
  'anmeldersachbearbeiter': 'kopf.anmelder.ansprechName',
  'anmeldertTelefonNummer': 'kopf.anmelder.phone',
  'anmeldertinName': 'kopf.anmelder.tin',
  'anmeldertinNiederlassungsnumme': 'kopf.anmelder.niederlassungsNummer',
  'anmeldertinNiederlassungsnummer': 'kopf.anmelder.niederlassungsNummer',
  'anmeldertinSachbearbeiter': 'kopf.anmelder.ansprechName',
  'anmeldertinTin': 'kopf.anmelder.tin',
  'anmeldertinname': 'kopf.anmelder.tin',
  'anzahlPackstucke': 'position[0].verpackung[0].anzahlderPackstucke',
  'anzahlPositionen': 'kopf.anzahlPositionen',
  'artDesBeforderungsmittelsAnDerGrenze': 'kopf.beforderungsmittelderGrenzeArt',
  'artDesBeförderungsmittelsAnDerGrenze': 'kopf.beforderungsmittelderGrenzeArt',
  'artderAnmeldung': 'kopf.artderAnmeldung',
  'artderAusfuhranmeldung': 'kopf.artderAusfuhranmeldung',
  'artderPackstucke': 'position[0].verpackung[0].artderVerpackung',
  'artderanmeldung': 'kopf.artderAnmeldung',
  'artdesbeforderungsmittelandergrenze': 'kopf.beforderungsmittelderGrenzeArt',
  'ausfuehrer': 'kopf.ausfuhrer',
  'ausfuhranmeldung': 'kopf.artderAusfuhranmeldung',
  'ausfuhrer.land': 'kopf.ausfuhrer.land',
  'ausfuhrer.name': 'kopf.ausfuhrer.name',
  'ausfuhrer.ort': 'kopf.ausfuhrer.ort',
  'ausfuhrer.plz': 'kopf.ausfuhrer.plz',
  'ausfuhrer.strasse': 'kopf.ausfuhrer.strasse',
  'ausfuhrer.tin': 'kopf.ausfuhrer.tin',
  'ausfuhrerLand': 'kopf.ausfuhrer.land',
  'ausfuhrerName': 'kopf.ausfuhrer.name',
  'ausfuhrerOrt': 'kopf.ausfuhrer.ort',
  'ausfuhrerPlz': 'kopf.ausfuhrer.plz',
  'ausfuhrerStrasse': 'kopf.ausfuhrer.strasse',
  'ausfuhrerTin': 'kopf.ausfuhrer.tin',
  'ausfuhrland': 'kopf.ausfuhrland',
  'ausfuhrzollstelle': 'kopf.ausfuhrzollstelleDienststellennummer',
  'ausfuhrzollstelleDienststellennummer': 'kopf.ausfuhrzollstelleDienststellennummer',
  'ausgangszollstelle': 'kopf.vorgeseheneAusgangszollstelleDienststellennummer',
  'beantragtesVerfahren': 'beantragtesVerfahren',
  'beforderungsmittel': 'sendung.beforderungsmittel',
  'beforderungsmittelBeimAbgang': 'kopf.beforderungsmittelBeimAbgang[0].kennzeichen',
  'beforderungsmittelBeimAbgang.artderIdentifikation': 'kopf.beforderungsmittelBeimAbgang[0].artderIdentifikation',
  'beforderungsmittelBeimAbgang.kennzeichen': 'kopf.beforderungsmittelBeimAbgang[0].kennzeichen',
  'beforderungsmittelBeimAbgang.staatszugehorigkeit': 'kopf.beforderungsmittelBeimAbgang[0].staatszugehorigkeit',
  'beforderungsmittelBeimAbgangKennzeichen': 'kopf.beforderungsmittelBeimAbgang[0].kennzeichen',
  'beforderungsmittelbeimabgang_messages': 'kopf.beforderungsmittelBeimAbgang[0].kennzeichen',
  'beforderungsmittelbeimabgangkennzeichen': 'kopf.beforderungsmittelBeimAbgang[0].kennzeichen',
  'beforderungsmittelderGrenzeArt': 'kopf.beforderungsmittelderGrenzeArt',
  'beforderungsmittelderGrenzeKennzeichen': 'kopf.beforderungsmittelderGrenzeKennzeichen',
  'beforderungsmittelderGrenzeStaatszugehorigkeit': 'kopf.beforderungsmittelderGrenzeStaatszugehorigkeit',
  'beforderungsroute': 'kopf.beforderungsroute.ausgewahlteLander',
  'beforderungsroute.ausgewahlteLander': 'kopf.beforderungsroute.ausgewahlteLander',
  'beforderungsrouteTab': 'kopf.beforderungsroute.ausgewahlteLander',
  'beforderungsroutetab': 'kopf.beforderungsroute.ausgewahlteLander',
  'bestimmungsLand': 'kopf.bestimmungsLand',
  'beteiligtenKonstellation': 'kopf.beteiligtenKonstellation',
  'beteiligtenkonstellation': 'kopf.beteiligtenKonstellation',
  'bewilligung.art': 'kopf.bewilligung[0].art',
  'bewilligung.referenznummer': 'kopf.bewilligung[0].referenznummer',
  'bewilligungArt': 'kopf.bewilligung[0].art',
  'bewilligungReferenznummer': 'kopf.bewilligung[0].referenznummer',
  'bewilligungTab': 'kopf.bewilligung[0].art',
  'bewilligungart': 'kopf.bewilligung[0].art',
  'bewilligungreferenznummer': 'kopf.bewilligung[0].referenznummer',
  'bezugsnummer': 'kopf.bezugsnummer',
  'container': 'kopf.containerIndikator',
  'containerIndikator': 'kopf.containerIndikator',
  'containerindicator': 'kopf.containerIndikator',
  'containerkennzeichen': 'kopf.containerIndikator',
  'currency': 'kopf.rechnungswahrung',
  'datum': 'kopf.datum',
  'datumdesAusgangsField': 'kopf.kopfDatumdesAusgangs',
  'datumdesausgangsField': 'kopf.kopfDatumdesAusgangs',
  'datumdesausgangsfield': 'kopf.kopfDatumdesAusgangs',
  'dienststellennummer': 'kopf.dienststellennummer',
  'dienststellennummerAusfuhrzollstelle': 'kopf.ausfuhrzollstelleDienststellennummer',
  'eigenmasse': 'wareEigenmasse',
  'empfaenger': 'kopf.empfanger',
  'empfanger.identifikationsart': 'kopf.empfanger.identifikationsart',
  'empfanger.land': 'kopf.empfanger.land',
  'empfanger.name': 'kopf.empfanger.name',
  'empfanger.ort': 'kopf.empfanger.ort',
  'empfanger.plz': 'kopf.empfanger.plz',
  'empfanger.strasse': 'kopf.empfanger.strasse',
  'empfangerIdentifikationsart': 'kopf.empfanger.identifikationsart',
  'empfangerLand': 'kopf.empfanger.land',
  'empfangerName': 'kopf.empfanger.name',
  'empfangerOrt': 'kopf.empfanger.ort',
  'empfangerPlz': 'kopf.empfanger.plz',
  'empfangerStrasse': 'kopf.empfanger.strasse',
  'eoriNiederlassungsnummer': 'kopf.eoriNiederlassungsnummer',
  'erganzungszollstelleAusfuhrDienststellennummer': 'kopf.erganzungszollstelleAusfuhrDienststellennummer',
  'erstelldatum': 'kopf.erstelldatum',
  'exitcustomsoffice': 'kopf.vorgeseheneAusgangszollstelleDienststellennummer',
  'exportcustomsoffice': 'kopf.ausfuhrzollstelleDienststellennummer',
  'gesamtRohmasse': 'kopf.gesamtRohmasse',
  'gesamtbetrag': 'kopf.inRechnunggestellterGesamtbetrag',
  'gestellungszollstelle': 'kopf.gestellungszollstelle',
  'grenzStaatszugehorigkeit': 'kopf.beforderungsmittelderGrenzeStaatszugehorigkeit',
  'grenzuberschreitendesBeforderungsmittelArt': 'kopf.beforderungsmittelderGrenzeArt',
  'inRechnunggestellterGesamtbetrag': 'kopf.inRechnunggestellterGesamtbetrag',
  // ATLAS calls the invoice total "Angaben zum Geschaeftsvorgang / Rechnungspreis"
  // and reports it under this name. Unmapped, the error had no form field to
  // attach to: it appeared in the red banner at the top while the Errors tab
  // said 0 and every section showed clean — visible but unactionable, the same
  // display defect as the earlier unmapped paths. It is HEADER level
  // (positionNumber='', headerNumber='1'), not per line item.
  'angabenGeschaftsvorgangRechnungspreis': 'kopf.inRechnunggestellterGesamtbetrag',
  'kopf.angabenGeschaftsvorgangRechnungspreis': 'kopf.inRechnunggestellterGesamtbetrag',
  'angabengeschaftsvorgangrechnungspreis': 'kopf.inRechnunggestellterGesamtbetrag',
  'incotermcode': 'kopf.lieferbedingungIncotermCode',
  'inlandischerVerkehrszweig': 'kopf.inlandischerVerkehrszweig',
  'invoicecurrency': 'kopf.rechnungswahrung',
  'kopf.angemeldetes': '__fanout_beantragtesVerfahren__',
  'kopf.angemeldetes verfahren': '__fanout_beantragtesVerfahren__',
  'kopf.anmelder.ansprechpartnerName': 'kopf.anmelder.ansprechName',
  'kopf.anmelder.beauftragterName': 'kopf.anmelder.beauftragterName',
  'kopf.anmelder.name': 'kopf.anmelder.beauftragterName',
  'kopf.anmelderSachbearbeiter': 'kopf.anmelder.ansprechName',
  'kopf.anmeldername': 'kopf.anmelder.beauftragterName',
  'kopf.anmeldertinName': 'kopf.anmelder.tin',
  'kopf.anmeldertinNiederlassungsnumme': 'kopf.anmelder.niederlassungsNummer',
  'kopf.anmeldertinSachbearbeiter': 'kopf.anmelder.ansprechName',
  'kopf.anmeldertinname': 'kopf.anmelder.tin',
  'kopf.artDesBeforderungsmittelsAnDerGrenze': 'kopf.beforderungsmittelderGrenzeArt',
  'kopf.artDesBeförderungsmittelsAnDerGrenze': 'kopf.beforderungsmittelderGrenzeArt',
  'kopf.artdesbeforderungsmittelandergrenze': 'kopf.beforderungsmittelderGrenzeArt',
  'kopf.ausfuehrer': 'kopf.ausfuhrer',
  'kopf.ausfuhranmeldung': 'kopf.artderAusfuhranmeldung',
  'kopf.beforderungsmittelBeimAbgang.artderIdentifikation': 'kopf.beforderungsmittelBeimAbgang[0].artderIdentifikation',
  'kopf.beforderungsmittelBeimAbgang.kennzeichen': 'kopf.beforderungsmittelBeimAbgang[0].kennzeichen',
  'kopf.beforderungsmittelBeimAbgang.staatszugehorigkeit': 'kopf.beforderungsmittelBeimAbgang[0].staatszugehorigkeit',
  'kopf.beforderungsmittelBeimAbgangKennzeichen': 'kopf.beforderungsmittelBeimAbgang[0].kennzeichen',
  'kopf.beforderungsmittelbeimabgangkennzeichen': 'kopf.beforderungsmittelBeimAbgang[0].kennzeichen',
  'kopf.beforderungsmittelderGrenzeKennzeichen': 'kopf.beforderungsmittelderGrenzeKennzeichen',
  'kopf.beforderungsroute': 'kopf.beforderungsroute.ausgewahlteLander',
  'kopf.beforderungsroute.ausgewahlteLander': 'kopf.beforderungsroute.ausgewahlteLander',
  'kopf.beforderungsrouteTab': 'kopf.beforderungsroute.ausgewahlteLander',
  'kopf.bewilligung.art': 'kopf.bewilligung[0].art',
  'kopf.bewilligung.referenznummer': 'kopf.bewilligung[0].referenznummer',
  'kopf.container': 'kopf.containerIndikator',
  'kopf.containerIndikator': 'kopf.containerIndikator',
  'kopf.datumdesAusgangsField': 'kopf.kopfDatumdesAusgangs',
  'kopf.datumdesausgangsField': 'kopf.kopfDatumdesAusgangs',
  'kopf.datumdesausgangsfield': 'kopf.kopfDatumdesAusgangs',
  'kopf.dienststellennummerAusfuhrzollstelle': 'kopf.ausfuhrzollstelleDienststellennummer',
  'kopf.empfaenger': 'kopf.empfanger',
  'kopf.erganzungszollstelleAusfuhrDienststellennummer': 'kopf.erganzungszollstelleAusfuhrDienststellennummer',
  'kopf.grenzStaatszugehorigkeit': 'kopf.beforderungsmittelderGrenzeStaatszugehorigkeit',
  'kopf.grenzuberschreitendesBeforderungsmittelArt': 'kopf.beforderungsmittelderGrenzeArt',
  'kopf.massgeblichesDatum': 'kopf.massgeblichesDatum',
  'kopf.massgeblichesdatum': 'kopf.massgeblichesDatum',
  'kopf.mengeinbesondererMabeinheit': 'position[0].mengeinbesondererMabeinheit',
  'kopf.messages': 'kopf.dienststellennummer',
  'kopf.packstuckanzahl': 'position[0].verpackung[0].anzahlderPackstucke',
  'kopf.packstuckverpackungsart': 'position[0].verpackung[0].artderVerpackung',
  'kopf.packstuckzeichennummern': 'position[0].verpackung[0].versandzeichen',
  'kopf.staatszugehorigkeitGrenze': 'kopf.beforderungsmittelderGrenzeStaatszugehorigkeit',
  'kopf.staatszugehörigkeitGrenze': 'kopf.beforderungsmittelderGrenzeStaatszugehorigkeit',
  'kopf.subunternehmer.niederlassungsNummer': 'kopf.subunternehmer.niederlassungsNummer',
  'kopf.subunternehmer.tin': 'kopf.subunternehmer.tin',
  'kopf.unterlageart': 'position[0].unterlage[0].art',
  'kopf.unterlagetyp': 'position[0].unterlage[0].art',
  'kopf.vertreter.ansprechName': 'kopf.vertreter.ansprechName',
  'kopf.vertreter.niederlassungsNummer': 'kopf.vertreter.niederlassungsNummer',
  'kopf.vertreter.phone': 'kopf.vertreter.phone',
  'kopf.vertreter.tin': 'kopf.vertreter.tin',
  'kopf.vertreterDesAnmeldersNiederlassungsnummer': 'kopf.vertreter.niederlassungsNummer',
  'kopf.vertreterDesAnmeldersSachbearbeiter': 'kopf.vertreter.ansprechName',
  'kopf.vertreterDesAnmeldersTelefonNummer': 'kopf.vertreter.phone',
  'kopf.vertreterdesanmeldersNiederlassungsnummer': 'kopf.vertreter.niederlassungsNummer',
  'kopf.vertreterdesanmeldersSachbearbeiter': 'kopf.vertreter.ansprechName',
  'kopf.vertreterdesanmeldersTelefonNummer': 'kopf.vertreter.phone',
  'kopf.vertreterdesanmeldersniederlassungsnummer': 'kopf.vertreter.niederlassungsNummer',
  'kopf.vertreterdesanmelderssachbearbeiter': 'kopf.vertreter.ansprechName',
  'kopf.vertreterdesanmelderstelefonnummer': 'kopf.vertreter.phone',
  'kopf.vorangegangenes': '__fanout_vorhergehendesVerfahren__',
  'kopf.vorangegangenes verfahren': '__fanout_vorhergehendesVerfahren__',
  'kopf.vorgeseheneAusgangszollstelle': 'kopf.vorgeseheneAusgangszollstelleDienststellennummer',
  'kopf.warenortArtDerOrtsbestimmung': 'kopf.warenortArtDerOrtsbestimmung',
  'kopf.warenortArtdesOrtes': 'kopf.warenortArtdesOrtes',
  'kopf.warenortBewilligungsnummer': 'kopf.warenortBewilligungsnummer',
  'kopf.warenortZusatzlicheKennung': 'kopf.warenortZusatzlicheKennung',
  'kopfDatumdesAusgangs': 'kopf.kopfDatumdesAusgangs',
  'ladeort': 'kopf.ladeort.code',
  'lieferbedingungIncotermCode': 'kopf.lieferbedingungIncotermCode',
  'lieferbedingungOrt': 'kopf.lieferbedingungOrt',
  'lieferort': 'kopf.lieferbedingungOrt',
  'lieferung.bestimmungsLand': 'lieferung.bestimmungsLand',
  'lieferung.lieferbedingungen': 'lieferung.lieferbedingungen',
  'lieferung.ort': 'lieferung.ort',
  'lrn': 'kopf.lrn',
  'massgeblichesDateField': 'kopf.massgeblichesDatum',
  'massgeblichesDatum': 'kopf.massgeblichesDatum',
  'massgeblichesdatefield': 'kopf.massgeblichesDatum',
  'menge': 'menge',
  'mengeinbesondererMabeinheit': 'mengeinbesondererMabeinheit',
  'mengeneinheit': 'mengeneinheit',
  'messagerecipient': 'kopf.eoriNiederlassungsnummer',
  'messages': 'kopf.dienststellennummer',
  'nachrichtenempfaenger': 'kopf.eoriNiederlassungsnummer',
  'nachrichtenempfänger': 'kopf.eoriNiederlassungsnummer',
  'packstuck': 'position[0].verpackung[0].artderVerpackung',
  'packstuck_messages': 'position[0].verpackung[0].artderVerpackung',
  'packstuckanzahl': 'position[0].verpackung[0].anzahlderPackstucke',
  'packstuckverpackungsart': 'position[0].verpackung[0].artderVerpackung',
  'packstuckzeichennummern': 'position[0].verpackung[0].versandzeichen',
  'position.beantragtesVerfahren': 'position.beantragtesVerfahren',
  'position.eigenmasse': 'position.wareEigenmasse',
  'position.menge': 'position.menge',
  'position.mengeinbesondererMabeinheit': 'position.mengeinbesondererMabeinheit',
  'position.rohmasse': 'position.wareRohmasse',
  'position.statistischerWert': 'position.wareStatistischerWert',
  'position.ursprungsVersendungsregion': 'position.ursprungsVersendungsregion',
  'position.ursprungsland': 'position.ursprungsland',
  'position.vorhergehendesVerfahren': 'position.vorhergehendesVerfahren',
  'position.warenbezeichnung': 'position.wareWarenbezeichnung',
  'position.warennummer': 'position.wareWarennummerKN8',
  'position.warennummerKN8': 'position.wareWarennummerKN8',
  'position[0].anzahlderPackstucke': 'position[0].verpackung[0].anzahlderPackstucke',
  'position[0].artderPackstucke': 'position[0].verpackung[0].artderVerpackung',
  'position[0].packstuckanzahl': 'position[0].verpackung[0].anzahlderPackstucke',
  'position[0].packstuckverpackungsart': 'position[0].verpackung[0].artderVerpackung',
  'position[0].packstuckzeichennummern': 'position[0].verpackung[0].versandzeichen',
  'position[0].verpackung': 'position[0].verpackung[0].artderVerpackung',
  'position[0].versandzeichen': 'position[0].verpackung[0].versandzeichen',
  'position[1].anzahlderPackstucke': 'position[1].verpackung[0].anzahlderPackstucke',
  'position[1].artderPackstucke': 'position[1].verpackung[0].artderVerpackung',
  'position[1].packstuckanzahl': 'position[1].verpackung[0].anzahlderPackstucke',
  'position[1].packstuckverpackungsart': 'position[1].verpackung[0].artderVerpackung',
  'position[1].packstuckzeichennummern': 'position[1].verpackung[0].versandzeichen',
  'position[1].verpackung': 'position[1].verpackung[0].artderVerpackung',
  'position[1].versandzeichen': 'position[1].verpackung[0].versandzeichen',
  'position[2].anzahlderPackstucke': 'position[2].verpackung[0].anzahlderPackstucke',
  'position[2].artderPackstucke': 'position[2].verpackung[0].artderVerpackung',
  'position[2].packstuckanzahl': 'position[2].verpackung[0].anzahlderPackstucke',
  'position[2].packstuckverpackungsart': 'position[2].verpackung[0].artderVerpackung',
  'position[2].packstuckzeichennummern': 'position[2].verpackung[0].versandzeichen',
  'position[2].verpackung': 'position[2].verpackung[0].artderVerpackung',
  'position[2].versandzeichen': 'position[2].verpackung[0].versandzeichen',
  'positionsnummer': 'warePositionsnummer',
  'rechnungspreis': 'wareRechnungspreis',
  'rechnungswahrung': 'kopf.rechnungswahrung',
  'referencenumber': 'kopf.bezugsnummer',
  'referenznummerUCR': 'referenznummerUCR',
  'registriernummerextern': 'registriernummerextern',
  'rohmasse': 'wareRohmasse',
  'sendung.anzahlPackstucke': 'position[0].verpackung[0].anzahlderPackstucke',
  'sendung.containerNummer': 'sendung.containerNummer',
  'sendung.gesamtRohmasse': 'sendung.gesamtRohmasse',
  'sendung.versandzeichen': 'position[0].verpackung[0].versandzeichen',
  'sicherheit': 'kopf.sicherheit',
  'sicherheitsindikator': 'kopf.sicherheit',
  'sonstigerVerweis.art': 'sonstigerVerweis[0].art',
  'sonstigerVerweis.detail': 'sonstigerVerweis[0].detail',
  'sonstigerVerweis.referenznummer': 'sonstigerVerweis[0].referenznummer',
  'staatszugehorigkeitGrenze': 'kopf.beforderungsmittelderGrenzeStaatszugehorigkeit',
  'staatszugehörigkeitGrenze': 'kopf.beforderungsmittelderGrenzeStaatszugehorigkeit',
  'statistischerWert': 'wareStatistischerWert',
  'subunternehmer': 'kopf.subunternehmer',
  'subunternehmer.niederlassungsNummer': 'kopf.subunternehmer.niederlassungsNummer',
  'subunternehmer.tin': 'kopf.subunternehmer.tin',
  'supplementarycustomsoffice': 'kopf.erganzungszollstelleAusfuhrDienststellennummer',
  'supplementaryoffice': 'kopf.erganzungszollstelleAusfuhrDienststellennummer',
  'unterlage.art': 'unterlage[0].art',
  'unterlage.qualifikator': 'unterlage[0].qualifikator',
  'unterlage.referenznummer': 'unterlage[0].referenznummer',
  'unterlageart': 'position[0].unterlage[0].art',
  'unterlagetyp': 'position[0].unterlage[0].art',
  'ursprungsRegion': 'position[0].ursprungsVersendungsregion',
  'ursprungsVersendungsregion': 'position[0].ursprungsVersendungsregion',
  'ursprungsland': 'ursprungsland',
  'ursprungsversendungsregion': 'position[0].ursprungsVersendungsregion',
  'userid': 'kopf.userid',
  'verfahren': 'kopf.verfahren',
  // 'verkehrszweig' USED to map to 'kopf.verkehrszweig', which is a label-only
  // entry in AtlasValidationPreview and not a field. ATLAS's own message names
  // it: "SHIPMENT / Border Transport Mode". The kopf.-prefixed forms are here
  // because the backend emits the path already prefixed, and this table is
  // looked up by exact key then by lowercase key — never with the prefix
  // stripped, so the bare entries alone never matched.
  'verkehrszweig': 'kopf.verkehrszweigAnDerGrenze',
  'kopf.verkehrszweig': 'kopf.verkehrszweigAnDerGrenze',
  'verkehrszweigAnDerGrenze': 'kopf.verkehrszweigAnDerGrenze',
  'verkehrszweigImInland': 'kopf.inlandischerVerkehrszweig',
  'kopf.verkehrszweigiminland': 'kopf.inlandischerVerkehrszweig',
  'verkehrszweiggrenze': 'kopf.verkehrszweigAnDerGrenze',
  'verkehrszweigiminland': 'kopf.inlandischerVerkehrszweig',
  'verkehrszweiginland': 'kopf.inlandischerVerkehrszweig',
  'verpackung': 'position[0].verpackung[0].artderVerpackung',
  'verpackung.anzahlderPackstucke': 'verpackung[0].anzahlderPackstucke',
  'verpackung.artderVerpackung': 'verpackung[0].artderVerpackung',
  'verpackung.packstuckverweisPositionsnummer': 'verpackung[0].packstuckverweisPositionsnummer',
  'verpackung.versandzeichen': 'verpackung[0].versandzeichen',
  'versionsnr': 'kopf.versionsnr',
  'vertreter': 'kopf.vertreter',
  'vertreter.ansprechName': 'kopf.vertreter.ansprechName',
  'vertreter.niederlassungsNummer': 'kopf.vertreter.niederlassungsNummer',
  'vertreter.phone': 'kopf.vertreter.phone',
  'vertreter.tin': 'kopf.vertreter.tin',
  'vertreterDesAnmeldersNiederlassungsnummer': 'kopf.vertreter.niederlassungsNummer',
  'vertreterDesAnmeldersSachbearbeiter': 'kopf.vertreter.ansprechName',
  'vertreterDesAnmeldersTelefonNummer': 'kopf.vertreter.phone',
  'vertreterName': 'kopf.vertreter.name',
  'vertreterNiederlassungsnummer': 'kopf.vertreter.niederlassungsNummer',
  'vertreterTin': 'kopf.vertreter.tin',
  'vertreterdesanmeldersNiederlassungsnummer': 'kopf.vertreter.niederlassungsNummer',
  'vertreterdesanmeldersSachbearbeiter': 'kopf.vertreter.ansprechName',
  'vertreterdesanmeldersTelefonNummer': 'kopf.vertreter.phone',
  'vertreterdesanmeldersniederlassungsnummer': 'kopf.vertreter.niederlassungsNummer',
  'vertreterdesanmelderssachbearbeiter': 'kopf.vertreter.ansprechName',
  'vertreterdesanmelderstelefonnummer': 'kopf.vertreter.phone',
  'vorangegangenes': '__fanout_vorhergehendesVerfahren__',
  'vorangegangenes verfahren': '__fanout_vorhergehendesVerfahren__',
  'vorgeseheneAusgangszollstelle': 'kopf.vorgeseheneAusgangszollstelleDienststellennummer',
  'vorgeseheneAusgangszollstelleDienststellennummer': 'kopf.vorgeseheneAusgangszollstelleDienststellennummer',
  'vorgeseheneausgangszollstelle': 'kopf.vorgeseheneAusgangszollstelleDienststellennummer',
  'vorhergehendesVerfahren': 'vorhergehendesVerfahren',
  'wahrung': 'kopf.rechnungswahrung',
  'wareEigenmasse': 'wareEigenmasse',
  'wareErsteZusatznummer': 'wareErsteZusatznummer',
  'wareGesamtpreis': 'wareGesamtpreis',
  'wareNationaleAngabe': 'wareNationaleAngabe',
  'warePositionsnummer': 'warePositionsnummer',
  'wareRohmasse': 'wareRohmasse',
  'wareStatistischerWert': 'wareStatistischerWert',
  'wareTarifnummer': 'wareTarifnummer',
  'wareVermerk': 'wareVermerk',
  'wareWarennummerKN8': 'wareWarennummerKN8',
  'wareZweiteZusatznummer': 'wareZweiteZusatznummer',
  'warecusnummer': 'warecusnummer',
  'warenbezeichnung': 'wareWarenbezeichnung',
  'warennummer': 'wareWarennummerKN8',
  'warennummerKN8': 'wareWarennummerKN8',
  'warenortArtDerOrtsbestimmung': 'kopf.warenortArtDerOrtsbestimmung',
  'warenortArtdesOrtes': 'kopf.warenortArtdesOrtes',
  'warenortBewilligungsnummer': 'kopf.warenortBewilligungsnummer',
  'warenortZusatzlicheKennung': 'kopf.warenortZusatzlicheKennung',
  'zeitpunktderAnmeldung': 'kopf.zeitpunktderAnmeldung',
  'zusatzlichesVerfahren': 'zusatzlichesVerfahren',
};
